// src/pages/admin/Customers/CustomersList.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Edit, Search } from 'lucide-react';
import AdminHeader from '../../../components/admin/common/AdminHeader';
import AdminTable from '../../../components/admin/common/AdminTable';
import AdminSearch from '../../../components/admin/common/AdminSearch';
import StatusBadge from '../../../components/admin/common/StatusBadge';
import AdminBreadcrumb from '../../../components/admin/common/AdminBreadcrumb';
import { customerService, type Customer } from '../../../services/customerService';
import { formatPrice } from '../../../utils/helpers';

const CustomersList: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async (searchQuery: string = '') => {
    try {
      setLoading(true);
      const result = await customerService.getCustomers({
        search: searchQuery
      });
      setCustomers(result.customers);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchTerm(query);
    fetchCustomers(query);
  };

  const columns = [
    {
      key: 'name',
      title: 'Customer',
      render: (customer: Customer) => (
        <div>
          <Link 
            to={`/admin/customers/${customer.id}`}
            className="font-medium text-gray-900 hover:text-brand-blue-500"
          >
            {customer.name}
          </Link>
          <div className="text-sm text-gray-500">{customer.email}</div>
        </div>
      )
    },
    {
      key: 'contact',
      title: 'Contact',
      render: (customer: Customer) => (
        <div className="flex flex-col space-y-1">
          <div className="flex items-center text-gray-600">
            <Phone className="w-4 h-4 mr-1" />
            <span>{customer.phone}</span>
          </div>
          <div className="flex items-center text-gray-600">
            <Mail className="w-4 h-4 mr-1" />
            <span>{customer.email}</span>
          </div>
        </div>
      )
    },
    {
      key: 'orders',
      title: 'Orders',
      render: (customer: Customer) => (
        <div>
          <div className="font-medium">{customer.totalOrders} orders</div>
          <div className="text-sm text-gray-500">
            Last: {new Date(customer.lastOrder).toLocaleDateString()}
          </div>
        </div>
      )
    },
    {
      key: 'spent',
      title: 'Total Spent',
      render: (customer: Customer) => (
        <div className="font-medium">
          {formatPrice(customer.totalSpent)}
        </div>
      )
    },
    {
      key: 'status',
      title: 'Status',
      render: (customer: Customer) => (
        <StatusBadge 
          status={customer.status} 
          size="sm"
        />
      )
    },
    {
      key: 'location',
      title: 'Location',
      render: (customer: Customer) => (
        <div className="text-sm text-gray-600">
          {customer.address.city}, {customer.address.country}
        </div>
      )
    },
    {
      key: 'actions',
      title: '',
      render: (customer: Customer) => (
        <Link
          to={`/admin/customers/${customer.id}`}
          className="p-2 text-gray-400 hover:text-brand-blue-500 transition-colors"
        >
          <Edit className="w-5 h-5" />
        </Link>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <AdminBreadcrumb items={[{ label: 'Customers' }]} />
      
      <AdminHeader 
        title="Customers"
        actions={
          <div className="flex items-center space-x-4">
            <div className="w-64">
              <AdminSearch 
                placeholder="Search customers..." 
                onSearch={handleSearch}
              />
            </div>
          </div>
        }
      />

      <div className="bg-white rounded-lg shadow-sm">
        <AdminTable
          columns={columns}
          data={customers}
          isLoading={loading}
          onRowClick={(customer) => window.location.href = `/admin/customers/${customer.id}`}
        />
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-600 rounded-lg">
          {error}
        </div>
      )}
    </div>
  );
};

export default CustomersList;